/**
 * LineRenderer Component
 *
 * Renders Line geometry on the map using MapLibre GL JS Source + Layer.
 * Supports line styling (solid/dashed/dotted), width, color, and optional center marker.
 *
 * Features:
 * - MapLibre LineString rendering
 * - Line style customization (dash arrays)
 * - Line width and color
 * - Optional center marker
 * - Hover highlighting with glow effect
 * - Performance optimized (useMemo)
 *
 * @module components/map/GeometryRenderer/LineRenderer
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useLineDrag } from '../../../../hooks/useLineDrag';
import { useGeometryContextMenu } from '../../../../hooks/useGeometryContextMenu';
import { useArtifactContextMenuForGeometry } from '../../../../hooks/useArtifactContextMenuForGeometry';
import { useGeometryHover } from '../../../../hooks/useGeometryHover';
import { useGeometryInteraction } from '../../../../hooks/useGeometryInteraction';
import { useGeometrySelection } from '../../../../hooks/useGeometrySelection';
import { useArtifactGeometryHoverPreview } from '@/hooks/useArtifactGeometryHoverPreview';
import { useGeoMarkupStore } from '../../../../store';
import { GeometryType } from '../../../../types/geometry';
import { LinePointHandles } from './LinePointHandles';
import { LineSegmentHandles } from './LineSegmentHandles';
import { MapContextMenu, createTaskMenuItems } from '../../MapContextMenu';
import { DeleteConfirmationDialog } from '../../../ui/DeleteConfirmationDialog';
import { createLineGeoJson } from './utils/lineGeoJsonHelpers';
import { getDashArray } from './utils/lineStyleHelpers';
import {
  DEFAULT_LINE_COLOR,
  LINE_DEFAULT_WIDTH,
  LINE_DEFAULT_OPACITY,
  LINE_HOVER_WIDTH_BOOST,
  LINE_HITBOX_WIDTH_BOOST,
  LINE_HITBOX_WIDTH_BOOST_TOUCH,
  GLOW_WIDTH_BOOST,
  GLOW_OPACITY,
  GLOW_BLUR_AMOUNT,
  BLUR_HOVERED,
  LINE_JOIN_STYLE,
  LINE_CAP_STYLE,
} from '../../../../constants';
import { useDeviceLayout } from '../../../../hooks/useDeviceLayout';
import type { TaskGeometry } from '../../../../types';

/**
 * Props for LineRenderer component
 */
export interface LineRendererProps {
  /** The line geometry to render */
  geometry: TaskGeometry;
  /** ID of the task this geometry belongs to */
  taskId: string;
  /** Whether this geometry is selected */
  isSelected: boolean;
  /** Current map zoom level */
  zoom: number;
}

/**
 * LineRenderer Component
 *
 * Renders a line geometry on the MapLibre map with multi-geometry support.
 */
export const LineRenderer = memo(function LineRenderer({
  geometry,
  taskId,
  isSelected: _isSelected, // Available for future selected state rendering
  zoom: _zoom, // Available for future center marker scaling
}: LineRendererProps) {
  // Generate GeoJSON from geometry (memoized)
  // Note: geometry changes trigger re-interpolation based on lineType
  const geoJsonLine = useMemo(
    () => createLineGeoJson({ geometry }),
    [geometry]
  );

  // Get dash array for line style (memoized)
  const dashArray = useMemo(
    () => getDashArray(geometry.lineStyle),
    [geometry.lineStyle]
  );

  // Extract geometry properties
  const lineColor = geometry.lineColor || DEFAULT_LINE_COLOR;
  const lineWidth = geometry.lineWidth || LINE_DEFAULT_WIDTH;
  const calculatedCenter = geometry.calculatedCenter;

  // Check if line has enough points (BEFORE hooks to comply with Rules of Hooks)
  const linePoints = geometry.linePoints || [];
  const hasEnoughPoints = linePoints.length >= 2;

  // Drag functionality for entire line (uses QueryRenderedFeatures strategy)
  const { isDraggable: isLineDraggable } = useLineDrag(geometry.id, taskId);

  // Device capabilities for touch-specific hitbox size
  const { isTouch } = useDeviceLayout();
  const hitboxWidthBoost = isTouch
    ? LINE_HITBOX_WIDTH_BOOST_TOUCH
    : LINE_HITBOX_WIDTH_BOOST;

  // Check if this geometry is currently being dragged (from Zustand)
  const isDragging = useGeoMarkupStore(
    (s) => s.draggingGeometryId === geometry.id
  );

  // Check if this geometry is selected in Selection mode (from Zustand)
  const selectedGeometryId = useGeoMarkupStore((s) => s.selectedGeometryId);
  const activeTool = useGeoMarkupStore((s) => s.activeTool);
  const isSelectedInSelectionMode =
    selectedGeometryId === geometry.id && activeTool === GeometryType.Selection;

  // Interactive layer ID for click detection
  const interactiveLayerId = `line-hitbox-${geometry.id}`;

  // PPM (right-click) context menu for line (GEOMETRY menu - during editing)
  useGeometryContextMenu({
    geometryId: geometry.id,
    geometryType: GeometryType.Line,
    interactiveLayerId: interactiveLayerId,
    isPreview: false,
  });

  // Task-level context menu for free roam mode (TASK menu - when form closed)
  const { t } = useTranslation();
  const task = useGeoMarkupStore((s) =>
    s.tasks.items.find((t) => t.id === taskId)
  );

  // Context menu state from Zustand (unified state)
  const contextMenu = useGeoMarkupStore((s) => s.ui.contextMenu);
  const hideContextMenu = useGeoMarkupStore((s) => s.ui.hideContextMenu);
  const isTaskDetailsOpen = useGeoMarkupStore((s) => s.tasks.detailsIsOpen);
  const selectedTaskId = useGeoMarkupStore((s) => s.tasks.selectedId);

  // Check if task context menu should be visible for THIS specific geometry
  const showTaskContextMenu =
    contextMenu.isVisible &&
    contextMenu.type === 'task' &&
    contextMenu.taskData?.taskId === taskId &&
    contextMenu.taskData?.geometryId === geometry.id;

  const {
    showDeleteDialog,
    handleShowDetails,
    handleEditArtifact: handleEditTask,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    handleCreateHere,
  } = useArtifactContextMenuForGeometry({
    artifactKind: 'task',
    interactiveLayerId,
    artifactId: taskId,
    geometryId: geometry.id,
    coordinates: calculatedCenter || { lat: 0, lng: 0 },
  });

  // Context-aware task menu items (only "Create task here" when previewing this task)
  const finalTaskMenuItems = useMemo(() => {
    if (!task) return [];

    const isTaskBeingViewed = isTaskDetailsOpen && selectedTaskId === taskId;

    if (isTaskBeingViewed) {
      return [
        {
          icon: <Plus size={14} />,
          label: t('contextMenu.createTaskHere'),
          onClick: handleCreateHere,
        },
      ];
    }

    return createTaskMenuItems(
      handleShowDetails,
      handleEditTask,
      handleDeleteClick,
      handleCreateHere,
      t
    );
  }, [
    task,
    isTaskDetailsOpen,
    selectedTaskId,
    taskId,
    handleShowDetails,
    handleEditTask,
    handleDeleteClick,
    handleCreateHere,
    t,
  ]);

  // Unified click/double-click interaction (replaces useGeometryClick + useGeometryDoubleClick)
  // - Single click: Open task preview (delayed 250ms to allow double-click detection)
  // - Double click: Activate edit mode (loads geometries, opens form)
  // - Mobile: Long press activates edit mode
  useGeometryInteraction({
    geometry,
    taskId,
    interactiveLayerId,
    isPreview: false,
  });

  // Selection mode - single click to select (does NOT activate drawing tool)
  useGeometrySelection({
    geometryId: geometry.id,
    interactiveLayerId,
  });

  // Hover highlighting
  const { isHovered, hoverPoint } = useGeometryHover({
    geometryId: geometry.id,
    interactiveLayerId,
    isDraggable: isLineDraggable,
    isPreview: false,
  });

  useArtifactGeometryHoverPreview({
    artifactKind: 'task',
    artifactId: taskId,
    isHovered,
    coordinates: hoverPoint ?? calculatedCenter,
  });

  // Early return AFTER all hooks (Rules of Hooks compliance)
  if (!hasEnoughPoints) {
    return null;
  }

  // Layer paint properties (type-safe with const assertion)
  // Visual feedback: selected state in Selection mode (similar to hover but persistent)
  const layerPaint = {
    'line-color': lineColor,
    'line-width':
      isHovered || isSelectedInSelectionMode
        ? lineWidth + LINE_HOVER_WIDTH_BOOST
        : lineWidth,
    'line-dasharray': dashArray,
    'line-opacity': LINE_DEFAULT_OPACITY,
    ...((isHovered || isSelectedInSelectionMode) && {
      'line-blur': BLUR_HOVERED,
    }),
  } as const;

  // Interactive hitbox layer paint (wider, transparent for easier clicking)
  const hitboxPaint = {
    'line-color': 'transparent',
    'line-width': lineWidth + hitboxWidthBoost,
    'line-opacity': 0,
  } as const;

  return (
    <>
      {/* MapLibre Source + Layers for Line */}
      <Source
        id={`line-source-${geometry.id}`}
        type="geojson"
        data={geoJsonLine}
      >
        {/* Glow effect on hover or selection (rendered first, below visible line) */}
        {(isHovered || isSelectedInSelectionMode) && (
          <Layer
            id={`line-glow-${geometry.id}`}
            type="line"
            paint={{
              'line-color': lineColor,
              'line-width': lineWidth + GLOW_WIDTH_BOOST,
              'line-opacity': GLOW_OPACITY,
              'line-blur': GLOW_BLUR_AMOUNT,
            }}
            layout={{
              'line-cap': LINE_CAP_STYLE,
              'line-join': LINE_JOIN_STYLE,
            }}
          />
        )}

        {/* Visible line layer */}
        <Layer
          id={`line-layer-${geometry.id}`}
          type="line"
          paint={layerPaint}
          layout={{
            'line-cap': LINE_CAP_STYLE,
            'line-join': LINE_JOIN_STYLE,
          }}
        />

        {/* Interactive hitbox layer (transparent, wider for easier clicking/dragging) */}
        <Layer
          id={interactiveLayerId}
          type="line"
          paint={hitboxPaint}
          layout={{
            'line-cap': LINE_CAP_STYLE,
            'line-join': LINE_JOIN_STYLE,
          }}
        />
      </Source>

      {/* Line Point Handles - hover to show, drag individual points */}
      <LinePointHandles geometry={geometry} isDraggable={isLineDraggable} />

      {/* Line Segment Handles - always visible indicators on segment midpoints */}
      <LineSegmentHandles
        geometry={geometry}
        isDraggable={isLineDraggable}
        isDragging={isDragging}
      />

      {/* Task Context Menu (free roam mode - from unified Zustand state) */}
      {task && showTaskContextMenu && (
        <MapContextMenu
          isVisible={showTaskContextMenu}
          position={contextMenu.position}
          items={finalTaskMenuItems}
          onClose={hideContextMenu}
        />
      )}

      {/* Delete Confirmation Dialog (free roam mode) */}
      {task && showDeleteDialog && (
        <DeleteConfirmationDialog
          isOpen={showDeleteDialog}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          itemName={task.title}
          itemType="task"
        />
      )}
    </>
  );
});
